import React from "react";
import PropTypes from "prop-types";
import LoadingSpinner from "../LoadingSpinner";
import MetricCard from "../MetricCard";
import { formatCost, formatNumber } from "../../utils/formatters";
import { MODEL_FAMILIES } from "../../utils/constants";

/**
 * Thresholds (inclusive) for credit bar status levels, and the status name
 * returned for each tier.  Both tables live here so the full set of states
 * is visible in one place — no hidden literals elsewhere.
 *
 * To change a threshold: update CREDIT_BAR_THRESHOLDS only.
 * To add a status tier: add entries to both tables and handle the new
 * value in CSS (the class name equals the BAR_STATUS value).
 */
const CREDIT_BAR_THRESHOLDS = {
  EXHAUSTED: 0, // 0% remaining
  CRITICAL: 20, // ≤ 20% remaining
  LOW: 40, // ≤ 40% remaining
};

const BAR_STATUS = {
  EXHAUSTED: "exhausted",
  CRITICAL: "critical",
  LOW: "low",
  HEALTHY: "healthy", // > LOW threshold
};

/**
 * Compute progress bar percentage.
 * - null balance = unlimited → always 100%  (BUG-1/CQ-4: consolidated here)
 * - null allocation = no cap → 100%
 * - allocation 0 → 0%
 */
function creditPercent(balance, allocation) {
  if (balance == null) return 100; // unlimited balance
  if (allocation == null) return 100; // no cap
  if (allocation === 0) return 0;
  return Math.min(100, Math.round((balance / allocation) * 100));
}

function barStatus(percent) {
  if (percent <= CREDIT_BAR_THRESHOLDS.EXHAUSTED) return BAR_STATUS.EXHAUSTED;
  if (percent <= CREDIT_BAR_THRESHOLDS.CRITICAL) return BAR_STATUS.CRITICAL;
  if (percent <= CREDIT_BAR_THRESHOLDS.LOW) return BAR_STATUS.LOW;
  return BAR_STATUS.HEALTHY;
}

/**
 * Usage summary section displaying credit balances and metrics.
 *
 * INT-2: `credits` and `monthlyAllocations` come from independent API calls
 * (GET /account/credits and GET /billing/plans respectively). Each can fail
 * independently — credits may load while allocations show "—", or credits may
 * fail while the activity section still works. This is intentional: partial
 * data is better than an all-or-nothing failure.
 */
/**
 * Render the allocation label for a single credit family.
 * Extracted to avoid a nested ternary inside the MODEL_FAMILIES map (S3358).
 */
function renderAllocationLabel(allocation) {
  if (allocation === undefined) {
    return <span className="credit-balance-alloc">{"\u2014"}</span>;
  }
  if (allocation === null) {
    return <span className="credit-balance-alloc">No limit</span>;
  }
  return (
    <span className="credit-balance-alloc">
      {formatNumber(allocation)} / month
    </span>
  );
}

const UsageSection = ({
  usageSummary,
  loading,
  credits,
  monthlyAllocations,
  usageThisMonth,
  creditsLoading,
  isAdmin = false,
}) => {
  const hasCredits = credits && !creditsLoading;

  // Extract multi-branch renders to named variables to avoid nested ternaries
  // in JSX (S3358).
  let creditsContent;
  if (creditsLoading) {
    creditsContent = (
      <LoadingSpinner size="small" text="Loading credit balances..." />
    );
  } else if (hasCredits) {
    creditsContent = (
      <div className="credit-balances-grid">
        {MODEL_FAMILIES.map(({ key, label, color }) => {
          const balance = credits[key];
          const allocation = monthlyAllocations?.[key];
          const isUnlimited = balance == null;
          // creditPercent handles null balance internally (returns 100%)
          const pct = creditPercent(balance, allocation);
          const status = isUnlimited ? "healthy" : barStatus(pct);

          return (
            <div key={key} className="credit-balance-card settings-card">
              <div className="credit-balance-header">
                <span className="credit-balance-label">{label}</span>
                <span className="credit-balance-value">
                  {isUnlimited ? "Unlimited" : formatNumber(balance)}
                </span>
              </div>
              <div className="credit-progress-track">
                <div
                  className={`credit-progress-bar credit-progress-bar--${status}`}
                  style={{
                    width: `${pct}%`,
                    backgroundColor: isUnlimited ? color : undefined,
                  }}
                />
              </div>
              <div className="credit-balance-footer">
                {renderAllocationLabel(allocation)}
                {/* UX-1 fix: Only show usage when > 0 to avoid noise.
                    Also suppress when allocation is still loading (undefined)
                    to avoid showing usage without context of the total. */}
                {allocation !== undefined &&
                  usageThisMonth &&
                  usageThisMonth[key] > 0 && (
                    <span className="credit-balance-used">
                      {formatNumber(usageThisMonth[key])} used this month
                    </span>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    );
  } else {
    creditsContent = (
      <div className="settings-card">
        <p className="settings-empty">
          Credit balance information is not available.
        </p>
      </div>
    );
  }

  let activityContent;
  if (loading) {
    activityContent = (
      <LoadingSpinner size="small" text="Loading usage data..." />
    );
  } else if (usageSummary?.summary) {
    activityContent = (
      <>
        <div className="settings-metrics-grid">
          <MetricCard
            icon="🤖"
            label="Active Widgets"
            value={formatNumber(usageSummary.summary.activeWidgets)}
            color="blue"
          />
          <MetricCard
            icon="💬"
            label="Total Messages"
            value={formatNumber(usageSummary.summary.totalMessages)}
            color="green"
          />
          <MetricCard
            icon="🗣️"
            label="Conversations"
            value={formatNumber(usageSummary.summary.totalConversations)}
            color="purple"
          />
          {isAdmin && (
            <MetricCard
              icon="💰"
              label="Estimated Cost"
              value={formatCost(usageSummary.summary.totalCost)}
              color="orange"
            />
          )}
        </div>

        {/* Top Widgets */}
        {usageSummary.topWidgets && usageSummary.topWidgets.length > 0 && (
          <div className="settings-card settings-card-top-widgets">
            <h3 className="settings-card-title">Top Widgets by Usage</h3>
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Widget</th>
                  <th>Messages</th>
                  <th>Conversations</th>
                  {isAdmin && <th>Cost</th>}
                </tr>
              </thead>
              <tbody>
                {usageSummary.topWidgets.map((w) => (
                  <tr key={w.id}>
                    <td>{w.displayName || w.name}</td>
                    <td>{formatNumber(w.messages)}</td>
                    <td>{formatNumber(w.conversations)}</td>
                    {isAdmin && <td>{formatCost(w.cost)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  } else {
    activityContent = (
      <div className="settings-card">
        <p className="settings-empty">
          No usage data available for this period.
        </p>
      </div>
    );
  }

  return (
    <section className="settings-section">
      <h2 className="settings-section-title">Usage</h2>

      {/* Credit Balances */}
      {creditsContent}

      {/* Activity Metrics (Last 30 Days) */}
      <h3 className="settings-card-title settings-activity-title">
        Activity (Last 30 Days)
      </h3>
      {activityContent}
    </section>
  );
};

UsageSection.propTypes = {
  usageSummary: PropTypes.shape({
    summary: PropTypes.shape({
      activeWidgets: PropTypes.number,
      totalMessages: PropTypes.number,
      totalConversations: PropTypes.number,
      totalTokens: PropTypes.number,
      totalCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    topWidgets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        name: PropTypes.string,
        displayName: PropTypes.string,
        messages: PropTypes.number,
        conversations: PropTypes.number,
        cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  loading: PropTypes.bool,
  // null balance = unlimited for that family (CQ-5: allow null values)
  credits: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  ),
  // null allocation = no cap; undefined prop = plans still loading
  monthlyAllocations: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  ),
  // Credits consumed this calendar month per family
  usageThisMonth: PropTypes.objectOf(PropTypes.number),
  creditsLoading: PropTypes.bool,
  isAdmin: PropTypes.bool,
};

export default UsageSection;
